import React, { useState, useEffect } from 'react';
import { usePath } from 'hooks/useHookRouterCompat';
import styled, { css } from 'styled-components';
import User, * as UserMethods from 'data/User';
import FeatureFlag, { FeatureFlagValues } from 'data/FeatureFlag';
import NavLink from 'view/NavLink';
import PageSideNav from 'view/PageSideNav';
import Icon from 'view/Icon';
import { useAuth } from 'lib/Auth';
import { Color, FontWeight } from 'lib/styles';
import {
  ViewList,
  Folder,
  ViewModule,
  SupervisorAccount,
  Domain,
  Grain,
  Dns,
  AccountCircle,
  ExitToApp,
  ShoppingCart,
  BarChart,
  Settings as SettingsIcon,
  Group as OrganizationIcon,
  Autorenew
} from '@mui/icons-material';
import { ReactComponent as RequestQuoteIcon } from 'images/request_quote.svg';
import { ReactComponent as ResponseQuoteIconHighlist } from 'images/pricebook-highlight.svg';
import { grantAccess } from 'utils/authorization';
import { Main } from 'styles/themeColors';
import { useBusinessUnit } from 'context/businessUnit';

const InlineFlexContainer = styled.div`
  align-items: center;
  display: inline-flex;
  padding-right: 4px;
`;

const StyledSpan = styled.span<any>`
  margin-left: 5px;
  font-weight: ${FontWeight.bold};
`;

const CompanyIcon = styled(Icon).attrs({ size: 25 }) <any>`
  margin-right: 0 !important;

  path {
    ${({ fillColor }) => fillColor && css`
      fill: ${fillColor};
    `}
  }
`;

const CustomersIcon = styled(Icon).attrs({ size: 32 }) <any>`
  margin-right: 0 !important;

  path {
    ${({ fillColor }) => fillColor && css`
      fill: ${fillColor};
    `}
  }
`;

const ServicesIcon = styled(Icon).attrs({ size: 20 }) <any>`
  margin-right: 0 !important;
  margin-left: 4px;

  path {
    ${({ fillColor }) => fillColor && css`
      fill: ${fillColor};
    `}
  }
`;

const HomeIcon = styled(Icon).attrs({ size: 21 }) <any>`
  margin-right: 1px !important;
  margin-left: 2px !important;

  path {
    ${({ fillColor }) => fillColor && css`
      fill: ${fillColor};
    `}
  }
`;

const CampaignIcon = styled(Icon).attrs({ name: 'Campaign', size: 18 }) <any>`
  margin-right: 0 !important;

  path {
    ${({ fillColor }) => fillColor && css`
      fill: ${fillColor};
    `}
  }
`;

const separator = <div style={{ marginTop: '8px' }} />;

const SubNavWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubNavItem = styled.div`
  padding-left: 24px;
`;

const AccordionHeader = styled.div<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  color: ${({ isActive }) => isActive ? Main.brandLight : Color.darkerGray};
  font-weight: ${FontWeight.bold};
  line-height: 36px;
  text-transform: uppercase;
`;

const ExpandIcon = styled.span`
  margin-left: auto;
  font-size: 10px;
`;

interface Props {
  user: User;
  featureFlags: FeatureFlag;
};

export default function SideNavigation({ user, featureFlags }: Props) {
  const { logout } = useAuth();
  const path = usePath();
  const isSubscriptionActive = path.startsWith('/subscriptions');
  const isAffiliateActive = path.startsWith('/affiliates') || path.startsWith('/affiliate-members');
  const { selectedBusinessUnit } = useBusinessUnit();

  const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(isSubscriptionActive);
  const [isAffiliateExpanded, setIsAffiliateExpanded] = useState(isAffiliateActive);

  useEffect(() => {
    if (isSubscriptionActive) {
      setIsSubscriptionExpanded(true);
    }
  }, [isSubscriptionActive]);

  useEffect(() => {
    if (isAffiliateActive) {
      setIsAffiliateExpanded(true);
    }
  }, [isAffiliateActive]);
  const isVendor = UserMethods.isVendor(user);
  const isBrandLevelRole = UserMethods.isBrandLevelRoles(user);

  const navLinks = (
    <>
      <NavLink to="/" isSideNav={true}>
        <InlineFlexContainer>
          <HomeIcon name="Home" fillColor={path === "/" ? Main.brandLight : Color.darkerGray} /><StyledSpan>Home</StyledSpan>
        </InlineFlexContainer>
      </NavLink>
      {
        grantAccess(featureFlags, FeatureFlagValues.ORDER, user) ? (
          <NavLink to="/orders" isSideNav={true} atBasePath>
            <InlineFlexContainer>
              <ShoppingCart /><StyledSpan>Orders</StyledSpan>
            </InlineFlexContainer>
          </NavLink>
        ) : null
      }

      {separator}

      {
        grantAccess(featureFlags, FeatureFlagValues.COMPANY, user) ? (
          <NavLink isSideNav={true} atBasePath to="/companies">
            <InlineFlexContainer>
              <CompanyIcon name="Company" fillColor={path.includes('/companies') ? Main.brandLight : Color.darkerGray} />
              <StyledSpan>Companies</StyledSpan>
            </InlineFlexContainer>
          </NavLink>
        ) : null
      }

      {
        grantAccess(featureFlags, FeatureFlagValues.COMPANY, user) ? (
          <NavLink isSideNav={true} atBasePath to="/customers">
            <InlineFlexContainer style={{ marginLeft: '-5px' }}>
              <CustomersIcon name='CustomersNav' fillColor={path.includes('/customers') ? Main.brandLight : Color.darkerGray} />
              <StyledSpan>Customers</StyledSpan>
            </InlineFlexContainer>
          </NavLink>
        ) : null
      }

      {separator}

      {
        isVendor || isBrandLevelRole
          ? null
          : (
            <NavLink isSideNav={true} atBasePath to="/vendors">
              <InlineFlexContainer>
                <Dns /><StyledSpan>Vendors</StyledSpan>
              </InlineFlexContainer>
            </NavLink>
          )
      }

      {
        (isVendor || isBrandLevelRole) || featureFlags?.businessUnit?.enabled
          ? null
          : (
            <NavLink isSideNav={true} atBasePath to="/users">
              <InlineFlexContainer>
                <SupervisorAccount /><StyledSpan>Users</StyledSpan>
              </InlineFlexContainer>
            </NavLink>
          )
      }

      {
        featureFlags.businessUnit && featureFlags.businessUnit?.enabled
          ? (
            <NavLink isSideNav={true} atBasePath to="/organizations">
              <InlineFlexContainer>
                <OrganizationIcon /><StyledSpan>Organization</StyledSpan>
              </InlineFlexContainer>
            </NavLink>) : null
      }

      {isVendor || isBrandLevelRole ? null : separator}

      <NavLink isSideNav={true} atBasePath to="/products">
        <InlineFlexContainer>
          <ViewList /><StyledSpan>Products</StyledSpan>
        </InlineFlexContainer>
      </NavLink>

      {
        (UserMethods.hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user) ? (
          <SubNavWrapper>
            <AccordionHeader
              isActive={isSubscriptionActive}
              onClick={() => setIsSubscriptionExpanded(prev => !prev)}
            >
              <InlineFlexContainer>
                <Autorenew /><StyledSpan>Subscriptions</StyledSpan>
              </InlineFlexContainer>
              <ExpandIcon>{isSubscriptionExpanded ? '▲' : '▼'}</ExpandIcon>
            </AccordionHeader>
            {isSubscriptionExpanded && (
              <>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/subscriptions/products" current={path.includes('/subscriptions/subscription-details')}>
                    <InlineFlexContainer>
                      <ViewList /><StyledSpan>Products</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/subscriptions/orders">
                    <InlineFlexContainer>
                      <ShoppingCart /><StyledSpan>Orders</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
              </>
            )}
          </SubNavWrapper>
        ) : null
      }

      {
        grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user) ? (
          <SubNavWrapper>
            <AccordionHeader
              isActive={isAffiliateActive}
              onClick={() => setIsAffiliateExpanded(prev => !prev)}
            >
              <InlineFlexContainer>
                <ViewList /><StyledSpan>Affiliates</StyledSpan>
              </InlineFlexContainer>
              <ExpandIcon>{isAffiliateExpanded ? '▲' : '▼'}</ExpandIcon>
            </AccordionHeader>
            {isAffiliateExpanded && (
              <>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/affiliates/program">
                    <InlineFlexContainer>
                      <ViewList /><StyledSpan>Program</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/affiliates/products">
                    <InlineFlexContainer>
                      <ShoppingCart /><StyledSpan>Products</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/affiliate-members">
                    <InlineFlexContainer>
                      <SupervisorAccount /><StyledSpan>Members</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/affiliates/orders">
                    <InlineFlexContainer>
                      <ShoppingCart /><StyledSpan>Orders</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
                <SubNavItem>
                  <NavLink isSideNav={true} atBasePath to="/affiliates/settings">
                    <InlineFlexContainer>
                      <SettingsIcon /><StyledSpan>Settings</StyledSpan>
                    </InlineFlexContainer>
                  </NavLink>
                </SubNavItem>
              </>
            )}
          </SubNavWrapper>
        ) : null
      }

      {
        featureFlags.services && featureFlags.services.enabled
          ? (
            <NavLink isSideNav={true} atBasePath to="/addons">
              <InlineFlexContainer>
                <ServicesIcon name='Services' fillColor={path.includes('/addons') ? Main.brandLight : Color.darkerGray} />
                <StyledSpan>Add-ons</StyledSpan>
              </InlineFlexContainer>
            </NavLink>) : null
      }

      <NavLink isSideNav={true} atBasePath to="/brands">
        <InlineFlexContainer>
          <Folder /><StyledSpan>Brands</StyledSpan>
        </InlineFlexContainer>
      </NavLink>

      <NavLink isSideNav={true} atBasePath to="/categories">
        <InlineFlexContainer>
          <ViewModule /><StyledSpan>Categories</StyledSpan>
        </InlineFlexContainer>
      </NavLink>

      <NavLink isSideNav={true} atBasePath to="/attributes">
        <InlineFlexContainer>
          <Grain /><StyledSpan>Attributes</StyledSpan>
        </InlineFlexContainer>
      </NavLink>

      <NavLink isSideNav={true} atBasePath to="/warehouses">
        <InlineFlexContainer>
          <Domain /><StyledSpan>Warehouses</StyledSpan>
        </InlineFlexContainer>
      </NavLink>

      <NavLink isSideNav={true} atBasePath to="/pricebooks">
        <InlineFlexContainer>
          {
            path.includes('/pricebooks') ? (<ResponseQuoteIconHighlist />) : (<RequestQuoteIcon />)
          }
          <StyledSpan>Pricebooks</StyledSpan>
        </InlineFlexContainer>
      </NavLink>

      {separator}

      {
        featureFlags.powerBiReport && featureFlags.powerBiReport.enabled
          ? (
            <NavLink isSideNav={true} atBasePath to="/reports">
              <InlineFlexContainer>
                <BarChart /><StyledSpan>Reports</StyledSpan>
              </InlineFlexContainer>
            </NavLink>
          ) : null
      }

      {
        featureFlags.marketingCampaign && featureFlags.marketingCampaign.enabled
          ? (
            <NavLink isSideNav={true} atBasePath to="/marketing">
              <InlineFlexContainer>
                <CampaignIcon name='Marketing' fillColor={path.includes('/marketing') ? Main.brandLight : Color.darkerGray} />
                <StyledSpan>Marketing</StyledSpan>
              </InlineFlexContainer>
            </NavLink>
          ) : null
      }

    </>
  );

  const userLinks = (
    <>
      <NavLink isSideNav={true} atBasePath to="/settings">
        <InlineFlexContainer>
          <SettingsIcon />
          <StyledSpan>Settings</StyledSpan>
        </InlineFlexContainer>
      </NavLink>
      <NavLink isSideNav={true} atBasePath to="/account">
        <InlineFlexContainer>
          <AccountCircle />
          <StyledSpan>Account</StyledSpan>
        </InlineFlexContainer>
      </NavLink>
      <NavLink isSideNav={true} atBasePath to="#" onClick={logout}>
        <InlineFlexContainer>
          <ExitToApp />
          <StyledSpan>Logout</StyledSpan>
        </InlineFlexContainer>
      </NavLink>
    </>
  );

  return <PageSideNav navLinks={navLinks} userLinks={userLinks} />;
};
